import { Box, Switch } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { useFieldError } from "../context";
import { PrimitiveNode } from "../types";
import { useNullableField } from "../useNullableField";
import { FieldLabel } from "./FieldLabel";
import { PrimitiveInput } from "./PrimitiveInput";

/*
 * A single value (text, number, select, switch, or secret) with its label.
 * An optional field gets a switch that sets it to null and back.
 * With `bare`, no label (the parent shows the title).
 */
export const PrimitiveField = memo(
  ({ node, bare = false }: { node: PrimitiveNode; bare?: boolean }) => {
    const { t } = useTranslation();
    const { value, enabled, toggle, set } = useNullableField(node);
    const error = useFieldError(node.path);
    const canToggle = node.nullable && !node.required;

    return (
      <Box sx={{ mb: node.input === "switch" ? 1 : 2 }}>
        {!bare && (
          <FieldLabel
            title={node.title}
            description={node.description}
            required={node.required}
          />
        )}

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          {canToggle && (
            <Switch
              checked={enabled}
              onChange={(e) => toggle(e.target.checked)}
              size="small"
              inputProps={{
                "aria-label": t("schemaForm.enabledLabel", {
                  name: node.title,
                }),
              }}
              sx={{ mt: 1 }}
            />
          )}

          {(enabled || !canToggle) && (
            <Box sx={{ flex: 1 }}>
              <PrimitiveInput
                node={node}
                value={value}
                onChange={set}
                label={node.title}
                errorText={error}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

PrimitiveField.displayName = "PrimitiveField";
